/**
 * Admin API client — calls Edge API /admin/* endpoints.
 * All requests include the Supabase JWT for platform admin auth.
 */

import { supabase } from './supabase'

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Not authenticated')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

const BASE = import.meta.env.VITE_ADMIN_API_URL

async function get<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE}${path}`, { headers })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE}${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function del(path: string): Promise<void> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE}${path}`, { method: 'DELETE', headers })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${BASE}${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

// ── Admin API functions ───────────────────────────────────────────────────────

import type {
  OverviewStats,
  TenantListItem,
  TenantDetail,
  Plan,
  AnalyticsData,
  CreateTenantPayload,
  UpdateTenantPayload,
  AssignPlanPayload,
  CreatePlanPayload,
  UpdatePlanPayload,
  PageComponent,
  CreateComponentPayload,
  UpdateComponentPayload,
  RegistrationRequest,
  UpgradeRequest,
  ThemePreset,
  ThemePresetSummary,
  CreateThemePresetPayload,
  UpdateThemePresetPayload,
} from '@/types/admin'

export const adminApi = {
  // Overview
  getOverview: () => get<OverviewStats>('/admin/overview'),

  // Tenants
  getTenants: () => get<TenantListItem[]>('/admin/tenants'),
  getTenant: (id: string) => get<TenantDetail>(`/admin/tenants/${id}`),
  createTenant: (data: CreateTenantPayload) => post<{ tenant_id: string; slug: string }>('/admin/tenants', data),
  updateTenant: (id: string, data: UpdateTenantPayload) => patch<TenantListItem[]>(`/admin/tenants/${id}`, data),
  assignPlan: (tenantId: string, data: AssignPlanPayload) =>
    post<unknown>(`/admin/tenants/${tenantId}/subscription`, data),

  // Plans
  getPlans: () => get<Plan[]>('/admin/plans'),
  createPlan: (data: CreatePlanPayload) => post<{ plan_id: string }>('/admin/plans', data),
  updatePlan: (id: string, data: UpdatePlanPayload) => patch<Plan>(`/admin/plans/${id}`, data),

  // Analytics
  getAnalytics: () => get<AnalyticsData>('/admin/analytics'),

  // Components
  getComponents: (params?: { category?: string; active?: boolean }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.active !== undefined) qs.set('active', String(params.active))
    const query = qs.toString() ? `?${qs}` : ''
    return get<PageComponent[]>(`/admin/components${query}`)
  },
  getComponent: (id: string) => get<PageComponent>(`/admin/components/${id}`),
  createComponent: (data: CreateComponentPayload) =>
    post<{ component_id: string }>('/admin/components', data),
  updateComponent: (id: string, data: UpdateComponentPayload) =>
    patch<PageComponent[]>(`/admin/components/${id}`, data),
  deleteComponent: (id: string) => del(`/admin/components/${id}`),
  getComponentPlans: (id: string) =>
    get<{ component_id: string; plan_ids: string[] }>(`/admin/components/${id}/plans`),
  updateComponentPlans: (id: string, plan_ids: string[]) =>
    put<{ component_id: string; plan_ids: string[] }>(`/admin/components/${id}/plans`, {
      plan_ids,
    }),

  // Theme Presets
  themePresets: {
    list: () => get<ThemePresetSummary[]>('/theme-presets/admin-list'),
    get: (id: string) => get<ThemePreset>(`/theme-presets/${id}`),
    create: (data: CreateThemePresetPayload) => post<ThemePreset>('/theme-presets', data),
    update: (id: string, data: UpdateThemePresetPayload) => patch<ThemePreset>(`/theme-presets/${id}`, data),
    delete: (id: string) => del(`/theme-presets/${id}`),
  },

  // Registrations
  getRegistrations: () => get<RegistrationRequest[]>('/admin/registrations'),
  approveRegistration: (id: string) =>
    post<{ tenant_id: string; slug: string; owner_email: string; default_password: string }>(
      `/admin/registrations/${id}/approve`,
      {}
    ),
  rejectRegistration: (id: string, notes?: string) =>
    post<{ success: boolean }>(`/admin/registrations/${id}/reject`, { notes }),

  // Platform Settings
  getPlatformSettings: () =>
    get<{ payment_info: string | null; qris_image_url: string | null }>('/admin/platform-settings'),
  updatePlatformSettings: (data: { payment_info?: string | null; qris_image_url?: string | null }) =>
    patch<{ payment_info: string | null; qris_image_url: string | null }>('/admin/platform-settings', data),

  // Upgrade Requests
  getUpgradeRequestProofUrl: (id: string) =>
    get<{ signed_url: string }>(`/admin/upgrade-requests/${id}/proof-url`),
  getUpgradeRequests: (status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    return get<UpgradeRequest[]>(`/admin/upgrade-requests${qs}`)
  },
  approveUpgradeRequest: (id: string) =>
    post<{ success: boolean; tenant_id: string; plan_id: string }>(
      `/admin/upgrade-requests/${id}/approve`,
      {}
    ),
  rejectUpgradeRequest: (id: string, admin_note?: string) =>
    post<{ success: boolean }>(`/admin/upgrade-requests/${id}/reject`, { admin_note }),
}
